import {debounce, getConfig, once, setBusy} from './core.js';
import {bindShowAll, bindStockToggle, isStockOnly, updateHiddenSummary} from './color-stock-filter.js';

const validColor = /^#[0-9a-f]{3}(?:[0-9a-f]{3})?$/i;
const validImage = /^[a-z0-9_./-]+\.(?:gif|jpe?g|png|webp)$/i;

function normaliseColor(raw) {
	return {
		id: String(raw.ID_BARVY ?? raw.id ?? ''),
		number: String(raw.CISLO_BARVY ?? raw.number ?? ''),
		name: String(raw._nazev ?? raw.name ?? ''),
		availability: String(raw._dostupnost ?? raw.availability ?? ''),
		availabilityCode: String(raw.DOSTUPNOST ?? raw.availabilityCode ?? ''),
		orderable: raw.orderable === true,
		availabilityIcon: String(raw.availabilityIcon ?? ''),
		background: validColor.test(raw.KOD_BARVY) ? raw.KOD_BARVY : '#fff',
		foreground: validColor.test(raw.kodInverzniBarvy) ? raw.kodInverzniBarvy : '#212529',
		image: validImage.test(raw.img ?? '') ? String(raw.img) : '',
	};
}

function optionLabel(color) {
	return [color.number, color.name].filter(Boolean).join(' – ');
}

function setOpen(picker, open) {
	const toggle = picker.querySelector('[data-color-picker-toggle]');
	const popup = picker.querySelector('[data-color-picker-popup]');
	toggle.setAttribute('aria-expanded', String(open));
	popup.hidden = !open;
	if (open) {
		picker.querySelector('[data-color-picker-search]').focus();
	}
}

function createOption(color, picker) {
	const item = document.createElement('li');
	item.className = 'color-picker__option';
	item.id = `${picker.id}-option-${color.id.replace(/[^a-z0-9_-]/gi, '-')}`;
	item.setAttribute('role', 'option');
	item.setAttribute('aria-selected', 'false');
	item.tabIndex = -1;
	item.dataset.value = color.id;
	item.dataset.search = `${color.number} ${color.name} ${color.availability}`.toLocaleLowerCase();
	item.dataset.orderable = String(color.orderable);
	item.dataset.colorInStock = String(color.availabilityCode === 'A');
	item.classList.toggle('is-not-in-stock', color.availabilityCode !== 'A');

	const swatch = document.createElement('span');
	swatch.className = 'color-picker__swatch';
	swatch.setAttribute('aria-hidden', 'true');
	swatch.style.setProperty('--swatch-background', color.background);
	swatch.style.setProperty('--swatch-foreground', color.foreground);
	if (color.image) {
		const imagePath = color.image.split('/').map((part) => encodeURIComponent(part)).join('/');
		swatch.style.setProperty('--swatch-image', `url("/image/${imagePath}")`);
	}

	const text = document.createElement('span');
	text.className = 'color-picker__label';
	text.textContent = optionLabel(color);
	const availability = document.createElement('span');
	availability.className = 'color-picker__availability';
	availability.textContent = color.availability;
	if (color.availabilityIcon) {
		const icon = document.createElement('i');
		icon.className = `color-picker__availability-icon bg-white rounded mr-1 p-0 ${color.availabilityIcon}`;
		icon.setAttribute('aria-hidden', 'true');
		availability.prepend(icon);
	}
	item.append(swatch, text, availability);
	return item;
}

function visibleOptions(picker) {
	return [...picker.querySelectorAll('[role="option"]')].filter((option) => !option.hidden);
}

function focusOption(picker, option) {
	if (!option) {
		return;
	}
	picker.querySelector('[data-color-picker-list]').setAttribute('aria-activedescendant', option.id);
	option.focus();
}

function selectOption(picker, option) {
	const container = picker.closest('.vyrobek, form') || picker.parentElement;
	const hidden = container.querySelector('input[name="barva"]');
	const cart = container.querySelector('.kosik');
	const toggle = picker.querySelector('[data-color-picker-toggle]');
	const selectedValue = toggle.querySelector('[data-color-picker-value]');
	const swatch = option.querySelector('.color-picker__swatch');
	const orderable = option.dataset.orderable === 'true';
	if (hidden) {
		hidden.value = option.dataset.value;
	}
	picker.querySelectorAll('[role="option"]').forEach((item) => item.setAttribute('aria-selected', String(item === option)));
	const label = document.createElement('span');
	label.className = 'text-truncate';
	label.textContent = option.querySelector('.color-picker__label').textContent;
	const availability = option.querySelector('.color-picker__availability');
	const icon = availability.querySelector('.color-picker__availability-icon');
	const accessibleAvailability = document.createElement('span');
	accessibleAvailability.className = 'sr-only';
	accessibleAvailability.textContent = availability.textContent ? ` – ${availability.textContent}` : '';
	selectedValue.replaceChildren(...(icon ? [icon.cloneNode(true)] : []), label, accessibleAvailability);
	toggle.title = [label.textContent, availability.textContent].filter(Boolean).join(' – ');
	selectedValue.style.setProperty('--selected-color-background', swatch.style.getPropertyValue('--swatch-background'));
	selectedValue.style.setProperty('--selected-color-foreground', swatch.style.getPropertyValue('--swatch-foreground'));
	selectedValue.style.setProperty('--selected-color-image', swatch.style.getPropertyValue('--swatch-image') || 'none');
	toggle.classList.add('has-selection');
	let quantity = null;
	if (cart) {
		cart.hidden = !orderable;
		cart.querySelectorAll('button, input').forEach((control) => control.disabled = !orderable);
		if (orderable) {
			quantity = cart.querySelector('[data-quantity]');
			quantity?.dispatchEvent(new Event('input', {bubbles: true}));
		}
	}
	setOpen(picker, false);
	if (quantity) {
		quantity.focus();
		quantity.select();
	} else {
		toggle.focus();
	}
	picker.dispatchEvent(new CustomEvent('color-picker:change', {bubbles: true, detail: {value: option.dataset.value, orderable}}));
}

function filterOptions(picker, query) {
	const needle = query.trim().toLocaleLowerCase();
	let count = 0;
	let hiddenCount = 0;
	picker.querySelectorAll('[role="option"]').forEach((option) => {
		const matchesSearch = needle === '' || option.dataset.search.includes(needle);
		const hiddenByStock = matchesSearch && isStockOnly() && option.dataset.orderable !== 'true';
		option.hidden = !matchesSearch || hiddenByStock;
		if (hiddenByStock) {
			hiddenCount += 1;
		}
		if (!option.hidden) {
			count += 1;
		}
	});
	const config = getConfig();
	const status = picker.querySelector('[data-color-picker-status]');
	status.textContent = count === 0 ? (config.colorPicker?.empty || '') : (config.colorPicker?.found || '').replace('%count%', String(count));
	updateHiddenSummary(picker, hiddenCount);
}

async function loadOptions(picker) {
	if (picker.dataset.loaded === 'true') {
		return;
	}
	const config = getConfig();
	const status = picker.querySelector('[data-color-picker-status]');
	const toggle = picker.querySelector('[data-color-picker-toggle]');
	status.textContent = config.colorPicker?.loading || '';
	setBusy(picker, true);
	toggle.disabled = true;
	try {
		const response = await fetch(picker.dataset.colorPickerUrl, {headers: {'X-Requested-With': 'XMLHttpRequest'}});
		if (!response.ok) {
			throw new Error(`HTTP ${response.status}`);
		}
		const payload = await response.json();
		const colors = Array.isArray(payload) ? payload.map(normaliseColor) : [];
		const list = picker.querySelector('[data-color-picker-list]');
		list.replaceChildren(...colors.map((color) => createOption(color, picker)));
		picker.dataset.loaded = 'true';
		filterOptions(picker, picker.querySelector('[data-color-picker-search]').value);
	} catch (error) {
		status.textContent = config.colorPicker?.error || '';
		console.error('Color picker could not load colors.', error);
	} finally {
		setBusy(picker, false);
		toggle.disabled = false;
	}
}

function bindPicker(picker) {
	if (!once(picker, 'color-picker')) {
		return;
	}
	const toggle = picker.querySelector('[data-color-picker-toggle]');
	const search = picker.querySelector('[data-color-picker-search]');
	const list = picker.querySelector('[data-color-picker-list]');
	picker.querySelectorAll('[data-color-stock-toggle]').forEach(bindStockToggle);
	picker.querySelectorAll('[data-color-stock-show-all]').forEach(bindShowAll);
	toggle.addEventListener('click', async () => {
		const open = toggle.getAttribute('aria-expanded') !== 'true';
		setOpen(picker, open);
		if (open) {
			await loadOptions(picker);
		}
	});
	search.addEventListener('input', debounce(() => filterOptions(picker, search.value)));
	picker.addEventListener('color-stock-filter:change', () => filterOptions(picker, search.value));
	list.addEventListener('click', (event) => {
		const option = event.target.closest('[role="option"]');
		if (option) {
			selectOption(picker, option);
		}
	});
	list.addEventListener('keydown', (event) => {
		const options = visibleOptions(picker);
		const index = options.indexOf(document.activeElement);
		const targets = {ArrowDown: options[Math.min(index + 1, options.length - 1)], ArrowUp: options[Math.max(index - 1, 0)], Home: options[0], End: options.at(-1)};
		if (targets[event.key]) {
			event.preventDefault();
			focusOption(picker, targets[event.key]);
		} else if (['Enter', ' '].includes(event.key) && document.activeElement?.matches('[role="option"]')) {
			event.preventDefault();
			selectOption(picker, document.activeElement);
		} else if (event.key === 'Escape') {
			setOpen(picker, false);
			toggle.focus();
		}
	});
	search.addEventListener('keydown', (event) => {
		if (event.key === 'ArrowDown') {
			event.preventDefault();
			focusOption(picker, visibleOptions(picker)[0]);
		} else if (event.key === 'Escape') {
			setOpen(picker, false);
			toggle.focus();
		}
	});
	document.addEventListener('click', (event) => {
		if (!picker.contains(event.target)) {
			setOpen(picker, false);
		}
	});
}

function bindShortcut(trigger) {
	if (!once(trigger, 'color-picker-shortcut')) {
		return;
	}
	const [card, value] = (trigger.dataset.colorId || '').split('_');
	const picker = document.getElementById(`color-picker-${card}`);
	if (!picker || !value) {
		return;
	}
	trigger.setAttribute('role', 'button');
	trigger.setAttribute('aria-controls', picker.id);
	trigger.tabIndex = 0;
	const activate = async (event) => {
		event.preventDefault();
		await loadOptions(picker);
		const option = [...picker.querySelectorAll('[role="option"]')]
			.find((item) => item.dataset.value === value);
		if (option) {
			selectOption(picker, option);
		}
	};
	trigger.addEventListener('click', activate);
	trigger.addEventListener('keydown', (event) => {
		if (['Enter', ' '].includes(event.key)) {
			activate(event);
		}
	});
}

export function init(root = document) {
	root.querySelectorAll('[data-color-picker]').forEach(bindPicker);
	root.querySelectorAll('[data-color-picker-shortcuts] [data-color-id]').forEach(bindShortcut);
}
